// ============================================================
// Cálculo fotométrico punto-a-punto a partir de curvas de candela IES
// (formato IESNA:LM-63-2002, ver ies-parser.ts).
//
// Este cálculo cubre la componente DIRECTA de la iluminancia (candela real
// del fabricante). La componente reflejada/interreflejada se aproxima
// mediante calculateZonalCavity() y NO usa aquí una integración de
// radiosidad completa — ver docs de decisión pendiente con Kevin.
// ============================================================

import { IesData } from './ies-parser';
import { RoomInput, SpacingResult, TipoTareaArt10, luminaireGridPositions } from './iluminacion';

export interface Position2D {
  x: number;
  y: number;
}

export interface LuminairePosition extends Position2D {
  alturaMontaje: number; // altura de la luminaria sobre el plano de trabajo (m)
}

export interface GridIlluminanceResult {
  iluminanciaPromedioDirecta: number;
  iluminanciaMinima: number;
  iluminanciaMaxima: number;
  uniformidad: number; // mínima / promedio
}

// Interpolación lineal simple sobre un eje de ángulos no necesariamente
// uniforme: encuentra el intervalo que contiene `value`, calcula el peso y
// devuelve el índice inferior junto con el peso de interpolación hacia el
// índice superior.
function findInterpolationBracket(
  axis: number[],
  value: number
): { i0: number; i1: number; t: number } {
  const clamped = Math.min(Math.max(value, axis[0]), axis[axis.length - 1]);

  for (let i = 0; i < axis.length - 1; i++) {
    if (clamped >= axis[i] && clamped <= axis[i + 1]) {
      const span = axis[i + 1] - axis[i];
      const t = span > 0 ? (clamped - axis[i]) / span : 0;
      return { i0: i, i1: i + 1, t };
    }
  }
  const last = axis.length - 1;
  return { i0: last, i1: last, t: 0 };
}

// Interpolación bilineal de la candela en (horizontalAngleDeg, verticalAngleDeg)
// usando candelaMatrix[i][j] = candela en horizontalAngles[i], verticalAngles[j].
function interpolateCandela(
  iesData: IesData,
  horizontalAngleDeg: number,
  verticalAngleDeg: number
): number {
  const h = findInterpolationBracket(iesData.horizontalAngles, horizontalAngleDeg);
  const v = findInterpolationBracket(iesData.verticalAngles, verticalAngleDeg);

  const c00 = iesData.candelaMatrix[h.i0][v.i0];
  const c01 = iesData.candelaMatrix[h.i0][v.i1];
  const c10 = iesData.candelaMatrix[h.i1][v.i0];
  const c11 = iesData.candelaMatrix[h.i1][v.i1];

  // Interpola primero en el eje vertical (para cada plano horizontal),
  // luego entre los dos planos horizontales.
  const cAtH0 = c00 + v.t * (c01 - c00);
  const cAtH1 = c10 + v.t * (c11 - c10);
  return cAtH0 + h.t * (cAtH1 - cAtH0);
}

// La mayoría de las luminarias publican solo el cuadrante 0°-90° (simetría
// de 4 planos). Se pliega cualquier ángulo horizontal real al rango cubierto
// por la tabla, reflejando cada 90° cuando corresponde.
function foldHorizontalAngle(angleDeg: number, maxTableAngle: number): number {
  let a = ((angleDeg % 360) + 360) % 360;
  if (maxTableAngle <= 90) {
    a = a % 180;
    if (a > 90) a = 180 - a;
  } else if (maxTableAngle <= 180) {
    if (a > 180) a = 360 - a;
  }
  return a;
}

// Calcula la iluminancia directa (lux) en `punto`, producida por una sola
// luminaria ubicada en `luminaria`, usando su curva de candela real (IES).
// E = (I(θ) · cos(θ)) / d²  — fórmula de la ley del inverso del cuadrado
// para una fuente puntual, con θ = ángulo de incidencia respecto al nadir.
export function calculateDirectIlluminance(
  luminaria: LuminairePosition,
  punto: Position2D,
  iesData: IesData
): number {
  const dx = punto.x - luminaria.x;
  const dy = punto.y - luminaria.y;
  const horizontalDistance = Math.sqrt(dx * dx + dy * dy);
  const h = luminaria.alturaMontaje;

  if (h <= 0) return 0;

  const distance = Math.sqrt(horizontalDistance * horizontalDistance + h * h);
  if (distance <= 0) return 0;

  // Ángulo respecto al nadir (0° = directamente debajo de la luminaria).
  const verticalAngleRad = Math.atan2(horizontalDistance, h);
  const verticalAngleDeg = (verticalAngleRad * 180) / Math.PI;

  // Ángulo horizontal (plano de la luminaria) hacia el punto.
  const horizontalAngleDegRaw = (Math.atan2(dy, dx) * 180) / Math.PI;
  const maxTableAngle = iesData.horizontalAngles[iesData.horizontalAngles.length - 1] ?? 90;
  const horizontalAngleDeg = foldHorizontalAngle(horizontalAngleDegRaw, maxTableAngle);

  const candela = interpolateCandela(iesData, horizontalAngleDeg, verticalAngleDeg);

  // cos(ángulo de incidencia) = h / distancia (geometría del triángulo
  // luminaria-nadir-punto).
  const cosIncidencia = h / distance;

  return (candela * cosIncidencia) / (distance * distance);
}

// Genera las posiciones (x, y) de las luminarias a partir del layout que
// devuelve calculateSpacing(): una grilla filas × columnas centrada en cada
// eje. Delega en luminaireGridPositions() (iluminacion.ts), la misma
// geometría que dibujan la planta 2D, la vista 3D y el PDF.
export function getLuminairePositions(spacing: SpacingResult): Position2D[] {
  return luminaireGridPositions(spacing);
}

// Suma la contribución directa de TODAS las luminarias del layout en cada
// punto de una grilla de muestreo (gridSize × gridSize) sobre el plano de
// trabajo, y devuelve las estadísticas de iluminancia directa resultante.
export function calculateGridIlluminance(
  room: RoomInput,
  spacing: SpacingResult,
  alturaMontaje: number,
  iesData: IesData,
  multiplier: number,
  numLamps: number,
  gridSize: number = 10
): GridIlluminanceResult {
  const luminairePositions = getLuminairePositions(spacing);

  if (luminairePositions.length === 0 || gridSize <= 0) {
    return {
      iluminanciaPromedioDirecta: 0,
      iluminanciaMinima: 0,
      iluminanciaMaxima: 0,
      uniformidad: 0,
    };
  }

  const valores: number[] = [];

  for (let i = 0; i < gridSize; i++) {
    for (let j = 0; j < gridSize; j++) {
      const punto: Position2D = {
        x: ((i + 0.5) * room.ancho) / gridSize,
        y: ((j + 0.5) * room.largo) / gridSize,
      };

      let eTotal = 0;
      for (const pos of luminairePositions) {
        const luminaria: LuminairePosition = { ...pos, alturaMontaje };
        eTotal += calculateDirectIlluminance(luminaria, punto, iesData) * multiplier * numLamps;
      }
      valores.push(eTotal);
    }
  }

  const suma = valores.reduce((acc, v) => acc + v, 0);
  const iluminanciaPromedioDirecta = suma / valores.length;
  const iluminanciaMinima = Math.min(...valores);
  const iluminanciaMaxima = Math.max(...valores);
  const uniformidad =
    iluminanciaPromedioDirecta > 0 ? iluminanciaMinima / iluminanciaPromedioDirecta : 0;

  return {
    iluminanciaPromedioDirecta,
    iluminanciaMinima,
    iluminanciaMaxima,
    uniformidad,
  };
}

// ============================================================
// Uniformidad Uo = Emín/Epromedio — EN 12464-1:2021 (§4.3.6), por remisión
// del DE 255 Art. 48: "Los criterios y límites de exposición … se acogerán a
// lo establecido en la normativa técnica nacional vigente; en caso de
// ausencia, se deberá referir a normas internacionales reconocidas." El
// Anexo 3 del AM 196 no fija un valor (Art. 10.1.c.iii solo pide "una
// distribución uniforme de la luz"). INDICADOR INFORMATIVO (decisión con
// Kevin, 24/09/2026): NO interviene en cumpleNormativa, porque solo
// considera la componente DIRECTA de las luminarias — sin la luz
// reflejada por techo/paredes/piso, la uniformidad resultante es más baja
// que la real (conservadora).
//
// Vigencia verificada el 28/09/2026: DE 255 Art. 48 leído en el texto
// oficial; AM 196 (2024) vigente; EN 12464-1:2021 es la versión vigente
// (reemplazó a la 2011). El DE 2393 (y su Art. 56, Emín/Emáx ≥ 0,7) NO está
// vigente: la Disposición Transitoria Décima Segunda del DE 255 lo mantuvo
// solo hasta que el MDT emitiera la normativa técnica (el AM 196).
//
// A diferencia de calculateGridIlluminance() (que sigue sin usarse y solo
// conoce la grilla teórica), esto usa las posiciones reales de la planta
// (grilla + reposicionamientos manuales) sobre la grilla de cálculo de
// EN 12464-1.
// ============================================================

// Forma mínima de una fila de luminaire_catalog (evita importar el módulo
// de datos, que arrastra el cliente de Supabase).
export interface PhotometricSource {
  vertical_angles: number[];
  horizontal_angles: number[];
  candela_matrix: number[][];
  multiplier: number;
}

const isFiniteNonNeg = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n >= 0;
const isAscending = (a: number[]) => a.every((v, i) => i === 0 || v > a[i - 1]);

// Valida y adapta los datos fotométricos del catálogo al formato IesData.
// Devuelve null si faltan o son incoherentes (el indicador no se muestra).
export function iesDataFromCatalog(src: PhotometricSource | null | undefined): IesData | null {
  if (!src) return null;
  const V = src.vertical_angles;
  const H = src.horizontal_angles;
  const M = src.candela_matrix;
  if (!Array.isArray(V) || !Array.isArray(H) || !Array.isArray(M)) return null;
  if (V.length === 0 || H.length === 0 || M.length !== H.length) return null;
  if (!V.every(isFiniteNonNeg) || !H.every(isFiniteNonNeg) || !isAscending(V) || !isAscending(H)) return null;
  if (!M.every(row => Array.isArray(row) && row.length === V.length && row.every(isFiniteNonNeg))) return null;
  if (!(typeof src.multiplier === 'number' && Number.isFinite(src.multiplier) && src.multiplier > 0)) return null;
  return {
    manufacturer: '', lumcat: '', luminaire: '', family: '',
    totalLumens: NaN, inputWattage: NaN, numLamps: 1,
    multiplier: src.multiplier,
    numVerticalAngles: V.length, numHorizontalAngles: H.length,
    verticalAngles: V, horizontalAngles: H, candelaMatrix: M,
  };
}

// Posiciones reales de las luminarias: la grilla de calculateSpacing()
// (mismo orden fila-mayor e índices que LuminairePlantView /
// LuminairePlant3DView / el PDF) con los reposicionamientos manuales
// aplicados encima.
export function getEffectiveLuminairePositions(
  spacing: SpacingResult,
  customPositions?: Record<number, { xMeters: number; yMeters: number }>
): Position2D[] {
  return getLuminairePositions(spacing).map((p, index) => {
    const c = customPositions?.[index];
    return c ? { x: c.xMeters, y: c.yMeters } : p;
  });
}

// Grilla de cálculo sobre el plano de trabajo — EN 12464-1:2021 (cuadrícula
// de cálculo de iluminancia), verificada contra fuentes técnicas el
// 28/09/2026:
//   - Se excluye una franja junto a las paredes de min(0,5 m; 15 % del lado
//     menor del local) (salvo que haya un área de tarea dentro de esa
//     franja — aquí no se modela). En locales con lado menor < 3,33 m la
//     franja es menor que 0,5 m.
//   - Tamaño máximo de celda p = 0,2 · 5^log10(d), con p ≤ 10 m, donde d es
//     la dimensión mayor del área de cálculo (ya sin la franja); si la
//     relación mayor/menor es ≥ 2, d pasa a ser la dimensión menor.
//   - Nº de puntos en esa dimensión = entero más cercano a d/p; el
//     espaciado resultante se usa para el nº de puntos de la otra dimensión
//     (celdas casi cuadradas). Los puntos van al centro de cada celda.
// Devuelve null si el local no tiene dimensiones positivas (con la franja
// proporcional siempre queda área de cálculo).
export const UNIFORMITY_WALL_BAND_MAX_M = 0.5;
export const UNIFORMITY_WALL_BAND_FRACCION = 0.15;

export function calculationGridEN12464(room: RoomInput): Position2D[] | null {
  const band = Math.min(UNIFORMITY_WALL_BAND_MAX_M, UNIFORMITY_WALL_BAND_FRACCION * Math.min(room.largo, room.ancho));
  const w = room.ancho - 2 * band; // eje x (misma convención que la planta)
  const l = room.largo - 2 * band; // eje y
  if (!(w > 0 && l > 0)) return null;

  const longer = Math.max(w, l);
  const shorter = Math.min(w, l);
  const d = longer / shorter >= 2 ? shorter : longer;
  const p = Math.min(10, 0.2 * Math.pow(5, Math.log10(d)));
  const nD = Math.max(1, Math.round(d / p));
  const s = d / nD;
  const nx = Math.max(1, Math.round(w / s));
  const ny = Math.max(1, Math.round(l / s));

  const points: Position2D[] = [];
  for (let i = 0; i < nx; i++) {
    for (let j = 0; j < ny; j++) {
      points.push({ x: band + ((i + 0.5) * w) / nx, y: band + ((j + 0.5) * l) / ny });
    }
  }
  return points;
}

export interface DirectUniformityResult {
  eMin: number;           // lux (directa, inicial — sin factor de mantenimiento)
  eMax: number;
  ePromedio: number;
  uo: number;             // Emín/Epromedio (EN 12464-1:2021)
  puntos: number;         // nº de puntos de la grilla de cálculo
}

// Referencia legal única de la uniformidad (pantalla, comparación A/B, PDF).
export const UNIFORMIDAD_REFERENCIA_LEGAL = 'EN 12464-1:2021, por remisión del DE 255 Art. 48';

// Uo mínima de la EN 12464-1:2021 para la actividad análoga a cada fila del
// Art. 10.4 del Anexo 3:
//   circulación y pasillos        → "Circulation corridors"             0,40
//   oficina                       → "Writing, reading, data processing" 0,60
//   mayor atención (laboratorios) → "Chemical industry — laboratories"  0,60
//   precisión (inspección fina)   → "Quality control / inspection"      0,70
// Valores confirmados por Kevin (técnico SSO) contra la EN 12464-1:2021 el
// 28/09/2026. Fuente de referencia inicial: TECHLUMEN.
export const UO_REFERENCIA_EN12464: Record<TipoTareaArt10, number> = {
  circulacion_pasillos: 0.4,
  tareas_generales_oficina: 0.6,
  mayor_atencion_detalle: 0.6,
  precision_extrema: 0.7,
};

// Texto único para pantalla, comparación A/B y PDF.
export const UNIFORMIDAD_DIRECTA_NOTA =
  'Considera solo la luz directa de las luminarias. No afecta el resultado de cumplimiento.';

// calculateDirectIlluminance() toma el ángulo horizontal con atan2(dy, dx):
// el plano C0 de todas las luminarias queda sobre +X (el ancho del local).
// Al girar el local, las lineales quedan a 90° de su posición anterior y la
// uniformidad estimada puede cambiar (el número de luminarias no). Texto único
// para pantalla (panel y comparación A/B) y PDF.
export const ORIENTACION_FOTOMETRIA_NOTA =
  'La fotometría se aplica con el plano C0 de la luminaria paralelo al ancho del local; en luminarias lineales, girarlas 90° puede cambiar la uniformidad estimada.';

// 0.7 → "0,70" (coma decimal).
export function formatRatioEs(v: number): string {
  return v.toFixed(2).replace('.', ',');
}

// Como formatRatioEs() pero TRUNCA (0,699 → "0,69"): solo para la
// uniformidad mostrada junto al verde/gris, para que el número nunca
// contradiga el color (redondeando, 0,699 se vería "0,70" en gris).
// El épsilon absorbe el error binario (0,29 · 100 = 28,999… → "0,29", no "0,28").
export function formatRatioTruncadoEs(v: number): string {
  return (Math.floor(v * 100 + 1e-9) / 100).toFixed(2).replace('.', ',');
}

export interface UniformidadPresentacion {
  alcanzaReferencia: boolean;
  linea: string;         // "Emín/Epromedio = X — referencia Uo ≥ Y (…)"
  lineaPdf: string;      // igual, sin "≥" (Helvetica de jsPDF no lo tiene)
  texto: string;         // veredicto verde/gris (pantalla)
  textoPdf: string;      // veredicto sin "≥"
  etiquetaCorta: string; // celda de la comparación A/B
  color: string;         // verde o gris neutro (nunca ámbar: no es una alerta)
}

// Único lugar donde se compara contra la referencia. Por qué "por debajo" es
// "no concluyente" y no una alerta: el indicador solo suma la luz DIRECTA. La
// componente reflejada (techo/paredes/piso) llega de forma aproximadamente
// pareja a todo el plano de trabajo, así que suma casi lo mismo R en el punto
// mínimo y en el promedio. En proporción sube más el mínimo que el promedio:
// (Emín + R) / (Epromedio + R) ≥ Emín / Epromedio cuando Emín ≤ Epromedio.
// La Uo real suele ser igual o mayor que la estimada: alcanzar la referencia
// aquí ya es favorable; no alcanzarla no demuestra un incumplimiento y se
// confirma midiendo en sitio.
export function presentarUniformidadDirecta(uo: number, tipoTarea: TipoTareaArt10): UniformidadPresentacion {
  const referencia = UO_REFERENCIA_EN12464[tipoTarea];
  const ref = formatRatioEs(referencia);
  const valor = formatRatioTruncadoEs(uo);
  const linea = `Emín/Epromedio = ${valor} — referencia Uo ≥ ${ref} (${UNIFORMIDAD_REFERENCIA_LEGAL})`;
  const lineaPdf = `Emín/Epromedio = ${valor} — referencia Uo no inferior a ${ref} (${UNIFORMIDAD_REFERENCIA_LEGAL})`;
  if (uo >= referencia) {
    return {
      alcanzaReferencia: true,
      linea,
      lineaPdf,
      texto: `Uo estimada ≥ ${ref} (luz directa; la real suele ser igual o mayor)`,
      textoPdf: `Uo estimada no inferior a ${ref} (luz directa; la real suele ser igual o mayor)`,
      etiquetaCorta: `≥ ${ref}`,
      color: '#2FBF71',
    };
  }
  const texto = 'No concluyente con luz directa; confirmar con medición en sitio';
  return { alcanzaReferencia: false, linea, lineaPdf, texto, textoPdf: texto, etiquetaCorta: 'No concluyente', color: '#94a3b8' };
}

// Iluminancia directa en cada punto de la grilla sumando todas las
// luminarias. Las candelas del LM-63 ya describen la luminaria completa y
// se escalan solo por `multiplier` (el flujo integrado del catálogo con
// multiplier coincide con total_lumens); NO se multiplica por el nº de
// lámparas. El factor de mantenimiento no se aplica: se cancela en Emín/Epromedio.
export function calculateDirectUniformity(
  room: RoomInput,
  luminaires: Position2D[],
  alturaMontaje: number,
  iesData: IesData
): DirectUniformityResult | null {
  const points = calculationGridEN12464(room);
  if (!points || luminaires.length === 0 || !(alturaMontaje > 0)) return null;

  let eMin = Infinity;
  let eMax = 0;
  let suma = 0;
  for (const punto of points) {
    let e = 0;
    for (const pos of luminaires) {
      e += calculateDirectIlluminance({ ...pos, alturaMontaje }, punto, iesData) * iesData.multiplier;
    }
    if (e < eMin) eMin = e;
    if (e > eMax) eMax = e;
    suma += e;
  }
  if (!(eMax > 0)) return null;
  const ePromedio = suma / points.length;
  return { eMin, eMax, ePromedio, uo: eMin / ePromedio, puntos: points.length };
}

// Atajo para la UI y el PDF: arma todo desde los mismos datos que la planta.
// Devuelve null cuando el indicador no aplica (sin datos fotométricos,
// dimensiones incompletas, local sin área de cálculo, sin luminarias).
export function estimateDirectUniformity(
  room: RoomInput,
  spacing: SpacingResult | null | undefined,
  photometry: PhotometricSource | null | undefined,
  customPositions?: Record<number, { xMeters: number; yMeters: number }>
): DirectUniformityResult | null {
  if (!spacing || !(room.largo > 0 && room.ancho > 0 && room.altura > 0)) return null;
  const iesData = iesDataFromCatalog(photometry);
  if (!iesData) return null;
  const alturaMontaje = room.altura - room.alturaPlanoTrabajo; // igual que calculateFullReport()
  return calculateDirectUniformity(
    room,
    getEffectiveLuminairePositions(spacing, customPositions),
    alturaMontaje,
    iesData
  );
}
